// Canal « HES + carte d'attention » : le HES en pleine résolution, et par-dessus l'attention d'un modèle d'IA par
// patchs (CSV importé, calé sur le HES : attentionFit.js), en image semi-transparente (BitmapLayer) à la résolution
// d'un niveau réduit du HES.
// - Valeur d'un patch : rang de son attention (centile, 0 à 1) : un softmax donne des valeurs très resserrées.
//   Valeurs signées (contribution d'un patch à la prédiction, positives et négatives) : le signe compte, pas le rang.
//   Champ lissé en vraies unités, puis t = (valeur − bas) / (haut − bas) : haut = échelle (98e centile de |valeur|
//   des patchs), bas = −échelle (0 au milieu : palette divergente, RdBu : bleu négatif, rouge positif) ou, avec un
//   seuil bas, le seuil (la première couleur de la LUT est la valeur choisie). Points chauds :
//   opacité croissante avec |valeur| ; isolignes de part et d'autre de 0. Réglages propres (s…).
// - Patchs forts gardés (emphasis = p) : dans le lissage, chaque patch pèse |valeur|^p (valeur rapportée au maximum ;
//   rang pour l'attention). p = 0 : moyenne simple, un patch extrême isolé parmi des voisins faibles s'y dilue et passe
//   sous les isolignes hautes ; p = 2 : un patch dix fois plus fort que ses voisins pèse cent fois plus et garde sa
//   zone. La couverture (opacité loin des patchs) reste comptée en nombre de patchs.
// - Seuil bas (valeurs signées, sBelow) : un patch sous le seuil (les plus négatifs) compte comme 0 (neutre) : il ne
//   crée pas de zone et, avec un poids |valeur|^p, ne dilue presque plus les autres ; les valeurs au-dessus restent.
// - Champ : patchs posés dans la grille (carrés transformés), lissés (σ en patchs, convolution normalisée : les
//   patchs sont souvent un échantillon du tissu, 215149H : 14 %) ; masqué hors du tissu du HES, effacé loin de tout
//   patch. σ = 0 : les patchs bruts, carrés pleins, sans masque.
// - Affichage « points chauds » (par défaut) : opacité croissante au-dessus d'un centile (40 %), LUT mpl-inferno : le
//   HES reste lisible où le modèle ne regarde pas ; « carte complète » : LUT sur tout le tissu, opacité fixe ;
//   « isolignes » : courbes de niveau du champ, traits vectoriels colorés par la LUT, qui laissent voir tout le tissu
//   (images figées : posters). Réglages propres (line…) : plus lissé (σ 2,5), niveaux 50, 65, 80 et 95, mpl-viridis
//   (bleu-vert à jaune : tranche sur le rose et le violet du HES, où le bas d'inferno se perd).
import { pixelSize } from '../../services';
import { gaussBlur } from './blur';
import { lutColor } from './lut';
import { fillHoles } from './tissue';
import { applyTransform, attentionRanks, invertTransform, tissueMask, transformScale } from './attentionFit';

export {
  decodePatches, encodePatches, parsePatches, placePatches, transformScale, tissueMask,
} from './attentionFit';

export const ATTENTION_ID = 'attention';  // identifiant du canal parmi les modalités
const SAMPLE_EDGE = 0.25;  // bord de l'échantillon : masque du tissu lissé sur un demi-patch, à ce seuil
// o : carte affichée (id de overlays/{id}) ; sigma en patchs ; threshold : centile d'où partent les points chauds.
// Isolignes : lineFrom (première), lineStep (écart), lineWidth (px d'écran), lineSigma, lineLut, lineLutRange.
// Valeurs signées : sLut, sLutRange (tous modes), sFrom, sStep (isolignes, en fraction de l'échelle) ; threshold en
// fraction de l'échelle pour les points chauds ; sBelow : valeur sous laquelle un patch compte comme 0 (vraies unités,
// null : aucun). emphasis : exposant p du poids des patchs forts dans le lissage.
export const ATTENTION_DEFAULTS = {
  o: null, display: 'hotspots', sigma: 1.5, threshold: 0.4, opacity: 0.85, lut: 'mpl-inferno', lutRange: null,
  lineFrom: 0.5, lineStep: 0.15, lineWidth: 2.5, lineSigma: 2.5, lineLut: 'mpl-viridis', lineLutRange: null,
  sLut: 'RdBu', sLutRange: null, sFrom: 0.25, sStep: 0.25, sBelow: null, emphasis: 0,
};

// Valeurs signées : il y en a des deux signes.
export const isSigned = v => v.some(x => x < 0) && v.some(x => x > 0);

// Clés enregistrées des réglages affichés (sigma, threshold, step, lut, lutRange) selon le mode et le signe.
function keysOf(settings, signed) {
  const contours = settings.display === 'contours';
  return {
    sigma: contours ? 'lineSigma' : 'sigma',
    threshold: contours ? (signed ? 'sFrom' : 'lineFrom') : 'threshold',
    step: signed ? 'sStep' : 'lineStep',
    lut: signed ? 'sLut' : contours ? 'lineLut' : 'lut',
    lutRange: signed ? 'sLutRange' : contours ? 'lineLutRange' : 'lutRange',
  };
}
// Réglages effectifs du mode affiché : les isolignes ont leur lissage, leurs niveaux et leur LUT ; les valeurs signées,
// leur LUT divergente et leurs niveaux.
export const attentionView = (settings, signed = false) => ({
  ...settings,
  ...Object.fromEntries(Object.entries(keysOf(settings, signed)).map(([k, key]) => [k, settings[key]])),
  signed,
});
// Clé enregistrée d'un réglage affiché dans le mode courant.
export const attentionKey = (settings, key, signed = false) => keysOf(settings, signed)[key] || key;

// Niveau réduit du HES (le plus petit d'au moins minPixels pixels), lu tuile par tuile : { rgb (3 composantes),
// comps, width, height, sx, sy (px pleine résolution par px du niveau) }. Tuiles du serveur (RGBA) ou OME-TIFF lu
// directement (RGB entrelacé).
export async function hesRaster(loader, minPixels = 3e6) {
  let level = loader.length - 1;
  while (level > 0) {
    const { width, height } = pixelSize(loader[level]);
    if (width * height >= minPixels) break;
    level -= 1;
  }
  const source = loader[level];
  const { width, height } = pixelSize(source);
  const size = source.tileSize;
  const rgb = new Uint8Array(width * height * 3);
  const jobs = [];
  for (let ty = 0; ty < Math.ceil(height / size); ty += 1) {
    for (let tx = 0; tx < Math.ceil(width / size); tx += 1) {
      jobs.push(source.getTile({ x: tx, y: ty, selection: { t: 0, z: 0, c: 0 } }).then(tile => {
        const comps = tile.data.length / (tile.width * tile.height);
        for (let row = 0; row < tile.height; row += 1) {
          const yy = ty * size + row;
          if (yy >= height) break;
          for (let col = 0; col < tile.width && tx * size + col < width; col += 1) {
            const s = (row * tile.width + col) * comps;
            const d = (yy * width + tx * size + col) * 3;
            rgb[d] = tile.data[s]; rgb[d + 1] = tile.data[s + 1]; rgb[d + 2] = tile.data[s + 2];
          }
        }
      }));
    }
  }
  await Promise.all(jobs);
  // JPEG YCbCr d'un OME-TIFF lu directement (les tuiles du serveur sont en RGB) : conversion JFIF.
  if (source.meta?.photometricInterpretation === 6) {
    const clamp = v => Math.min(255, Math.max(0, Math.round(v)));
    for (let i = 0; i < rgb.length; i += 3) {
      const Y = rgb[i]; const cb = rgb[i + 1] - 128; const cr = rgb[i + 2] - 128;
      rgb[i] = clamp(Y + 1.402 * cr); rgb[i + 1] = clamp(Y - 0.344136 * cb - 0.714136 * cr); rgb[i + 2] = clamp(Y + 1.772 * cb);
    }
  }
  const full = pixelSize(loader[0]);
  return { rgb, comps: 3, width, height, sx: full.width / width, sy: full.height / height };
}

// Champ de la carte, à la résolution du niveau réduit : { width, height, sx, sy, side (côté d'un patch en px du
// niveau), field (rang lissé, NaN sans donnée), cover (opacité relative, 0 à 1), region (1 : isolignes tracées ;
// échantillon plein : tissu fermé, trous intérieurs comblés), edge (masque lissé, bord de l'échantillon ; null pour
// les patchs bruts), coverage (part du tissu sous un patch), signed ; valeurs signées : scale (échelle), low, high
// (valeurs en t = 0 et 1), zero (t de la valeur 0), centered (pas de seuil : 0 au milieu) }.
export function attentionField(patches, transform, hes, mask, { sigma, signed = false, emphasis = 0, minValue = -Infinity }) {
  const { width: w, height: h, sx, sy } = hes;
  const n = w * h;
  // Valeur de chaque patch : rang (attention) ; valeurs signées, 0 sous le seuil bas.
  const rank = signed ? patches.v.map(v => (v < minValue ? 0 : v)) : attentionRanks(patches.v);
  const size = patches.patchSize;
  const B = invertTransform(transform);
  // Poids de chaque patch dans le lissage : |valeur|^p, valeur rapportée au maximum (plancher 2 % : un patch nul compte
  // encore un peu) ; C : nombre de patchs (couverture), distinct des poids quand ils varient.
  const weighted = emphasis > 0;
  let peak = 0;
  for (let i = 0; i < patches.count; i += 1) peak = Math.max(peak, Math.abs(rank[i]));
  const weightOf = i => (weighted ? Math.max(0.02, Math.abs(rank[i]) / (peak || 1)) ** emphasis : 1);
  const V = new Float32Array(n);
  const W = new Float32Array(n);
  const C = weighted ? new Float32Array(n) : W;
  // Chaque patch : les pixels du niveau dont le centre retombe dans son carré (coordonnées du CSV).
  for (let i = 0; i < patches.count; i += 1) {
    const x0 = patches.x[i]; const y0 = patches.y[i];
    const corners = [[x0, y0], [x0 + size, y0], [x0 + size, y0 + size], [x0, y0 + size]].map(([x, y]) => applyTransform(transform, x, y));
    const u0 = Math.max(0, Math.floor(Math.min(...corners.map(c => c[0])) / sx));
    const u1 = Math.min(w - 1, Math.ceil(Math.max(...corners.map(c => c[0])) / sx));
    const v0 = Math.max(0, Math.floor(Math.min(...corners.map(c => c[1])) / sy));
    const v1 = Math.min(h - 1, Math.ceil(Math.max(...corners.map(c => c[1])) / sy));
    for (let v = v0; v <= v1; v += 1) {
      for (let u = u0; u <= u1; u += 1) {
        const [cx, cy] = applyTransform(B, (u + 0.5) * sx, (v + 0.5) * sy);
        if (cx >= x0 && cx < x0 + size && cy >= y0 && cy < y0 + size) {
          const k = weightOf(i);
          V[v * w + u] += k * rank[i]; W[v * w + u] += k;
          if (weighted) C[v * w + u] += 1;
        }
      }
    }
  }
  let tissue = 0;
  let covered = 0;
  for (let i = 0; i < n; i += 1) if (mask[i]) { tissue += 1; if (C[i] > 0) covered += 1; }
  const field = new Float32Array(n).fill(NaN);
  const cover = new Float32Array(n);
  const region = new Uint8Array(n);
  const edge = new Float32Array(n);  // masque lissé du tissu : bord de l'échantillon au seuil SAMPLE_EDGE
  const side = (size * transformScale(transform)) / ((sx + sy) / 2);
  if (sigma > 0) {
    // σ en patchs -> px du niveau ; couverture : poids lissé rapporté à sa valeur habituelle sur le tissu. Isolignes :
    // tout l'échantillon, tissu fermé (lissé sur un demi-patch) et trous intérieurs comblés (lumières, fissures, tissu
    // sans patch : le champ lissé y est défini), pour que les lignes ne s'arrêtent qu'au bord extérieur de l'échantillon.
    const blurred = gaussBlur(Float32Array.from(mask), w, h, side / 2);
    const sample = new Uint8Array(n);
    for (let i = 0; i < n; i += 1) sample[i] = blurred[i] >= SAMPLE_EDGE ? 1 : 0;
    fillHoles(sample, w, h);
    const Cb = weighted ? gaussBlur(C, w, h, sigma * side) : null;
    const Vb = gaussBlur(V, w, h, sigma * side);
    const Wb = gaussBlur(W, w, h, sigma * side);
    const density = Cb || Wb;
    const soft = gaussBlur(Float32Array.from(mask), w, h, 0.7);  // bord du tissu adouci
    const weights = [];
    for (let i = 0; i < n; i += 97) if (mask[i] && density[i] > 0) weights.push(density[i]);
    weights.sort((a, b) => a - b);
    const typical = weights.length ? weights[weights.length >> 1] : 1;
    for (let i = 0; i < n; i += 1) {
      if (density[i] > 1e-6 && Wb[i] > 0) field[i] = Vb[i] / Wb[i];
      cover[i] = Math.min(1, density[i] / (0.5 * typical)) * soft[i];
      region[i] = sample[i];
      edge[i] = blurred[i];
    }
    // Échantillon loin de tout patch (fragment écarté par le modèle…) : champ d'un lissage quatre fois plus large, pour
    // que les isolignes y restent continues (la carte de chaleur, elle, s'y efface).
    let gaps = false;
    for (let i = 0; i < n && !gaps; i += 1) gaps = region[i] === 1 && Number.isNaN(field[i]);
    if (gaps) {
      const Vw = gaussBlur(V.slice(0), w, h, 4 * sigma * side);
      const Ww = gaussBlur(W.slice(0), w, h, 4 * sigma * side);
      for (let i = 0; i < n; i += 1) if (region[i] && Number.isNaN(field[i]) && Ww[i] > 1e-9) field[i] = Vw[i] / Ww[i];
    }
  } else {
    for (let i = 0; i < n; i += 1) if (C[i] > 0) { field[i] = V[i] / W[i]; cover[i] = 1; region[i] = 1; }
  }
  // Valeurs signées : [bas, haut] -> [0, 1]. Échelle fixe, 98e centile de |valeur| des patchs (une isoligne garde sa
  // valeur quand le lissage ou le poids des patchs forts change) : haut = échelle ; bas = −échelle, ou le seuil bas
  // (la LUT commence à la valeur choisie).
  let scale = null;
  let low = null;
  let high = null;
  let zero = 0.5;
  const centered = !Number.isFinite(minValue);
  if (signed) {
    const mags = Array.from(patches.v, Math.abs).sort((a, b) => a - b);
    scale = mags[Math.floor(0.98 * (mags.length - 1))] || mags[mags.length - 1] || 1;
    high = scale;
    low = centered ? -scale : Math.min(minValue, high * 0.99);
    zero = Math.min(1, Math.max(0, -low / (high - low)));
    for (let i = 0; i < n; i += 1) {
      if (!Number.isNaN(field[i])) field[i] = Math.min(1, Math.max(0, (field[i] - low) / (high - low)));
    }
  }
  return {
    width: w, height: h, sx, sy, side, field, cover, region, edge: sigma > 0 ? edge : null,
    coverage: tissue ? covered / tissue : 0, signed, scale, low, high, zero, centered,
  };
}

// Écart à 0 d'une position t, de 0 (valeur 0) à 1 (bout de la palette de son côté) : valeurs signées, zero = t de 0.
export const sideFraction = (t, zero = 0.5) => (t < zero ? (zero - t) / (zero || 1) : (t - zero) / ((1 - zero) || 1));

// Opacité d'un rang t (0 à 1) : « points chauds », croissante au-dessus du seuil ; « carte complète », fixe ;
// isolignes : celle des traits, à partir de la première. Valeurs signées : selon l'écart à 0 (sideFraction).
export const attentionAlpha = (t, { display, threshold, opacity, signed, zero }) => {
  const x = signed ? sideFraction(t, zero) : t;
  if (display === 'map') return opacity;
  if (display === 'contours') return x >= threshold ? opacity : 0;
  return opacity * Math.min(1, Math.max(0, (x - threshold) / Math.max(1e-3, 1 - threshold))) ** 0.8;
};

// Niveaux des isolignes (valeurs de t), réglages affichés (attentionView) : de la première jusqu'à 1 (exclu), tous les
// `step` ; valeurs signées : de part et d'autre de 0 (zero = t de 0), en fraction de chaque côté de la palette (côté
// négatif : jusqu'au seuil bas s'il y en a un).
export function contourLevels({ threshold, step, signed, zero = 0.5 }) {
  const steps = [];
  for (let k = 0; threshold + k * step < 0.999 && k < 40; k += 1) steps.push(Math.round((threshold + k * step) * 1000) / 1000);
  const fractions = steps.filter(l => l > 0);
  if (!signed) return fractions;
  const below = zero > 0 ? fractions.map(f => zero - f * zero).reverse() : [];
  return [...below, ...fractions.map(f => zero + f * (1 - zero))];
}

// Couleur d'une isoligne (RGB) : la LUT au niveau. Valeurs signées centrées sur 0 (sans seuil) : sans le centre pâle
// de la palette divergente (illisible sur le HES), écart à 0 ramené de 45 à 100 % de la demi-palette ; avec un seuil
// bas, la LUT telle quelle (le seuil prend sa première couleur).
export function contourColor(level, { lut, lutRange, signed, zero = 0.5, centered = true }) {
  let t = level;
  if (signed && centered) {
    const f = Math.min(1, 0.45 + 0.55 * sideFraction(level, zero));
    t = level < zero ? zero - f * zero : zero + f * (1 - zero);
  }
  return lutColor(lut, [255, 255, 255], false, t, lutRange);
}

// Isolignes du champ (marching squares sur les centres des px du niveau réduit) : [{ level, paths }] (polylignes en
// px pleine résolution). Zones fermées : hors de l'échantillon (`region`), le champ compte comme sous tous les niveaux,
// et la ligne passe alors sur le bord de l'échantillon (seuil du masque lissé `edge`, le même pour tous les niveaux) :
// une zone qui touche le bord est refermée en suivant le contour du tissu. Segments chaînés, puis lissés (Chaikin, deux
// passes) : la grille (32 px pleine résolution sur 215149H) ne se voit plus. Boucles de moins de deux côtés de patch
// écartées (bruit du champ).
export function attentionContours({ width: w, height: h, sx, sy, side, field, region, edge, signed, zero }, levels) {
  const minLength = 2 * side * (sx + sy) / 2;  // px pleine résolution
  // Grille bordée d'un px hors de l'échantillon (les zones qui touchent le bord de l'image se ferment aussi) :
  // champ (−1 dehors) et masque lissé.
  const W2 = w + 2;
  const valsUp = new Float32Array(W2 * (h + 2)).fill(-1);
  const soft = new Float32Array(W2 * (h + 2));
  for (let v = 0; v < h; v += 1) {
    for (let u = 0; u < w; u += 1) {
      const i = v * w + u;
      const j = (v + 1) * W2 + u + 1;
      if (region[i] === 1 && !Number.isNaN(field[i])) valsUp[j] = field[i];
      soft[j] = edge ? edge[i] : 0;
    }
  }
  // Valeurs signées, niveaux sous 0 : zones « sous le niveau », sur le champ retourné (1 − t), fermées de même.
  const flipped = signed ? valsUp.map(v => (v < 0 ? -1 : 1 - v)) : null;
  // Arêtes (coordonnées de la grille bordée) : horizontale (u, v)-(u+1, v) -> 2 j, verticale (u, v)-(u, v+1) -> 2 j + 1.
  const J = (u, v) => (v + 1) * W2 + u + 1;
  return levels.map(shown => {
    const flip = signed && shown < zero;
    const level = flip ? 1 - shown : shown;
    const vals = flip ? flipped : valsUp;
    const pos = new Map();
    const segs = [];
    const at = (key, x, y) => { if (!pos.has(key)) pos.set(key, [(x + 0.5) * sx, (y + 0.5) * sy]); return key; };
    // Passage sur l'arête j0-j1 : niveau du champ entre deux px de l'échantillon ; bord de l'échantillon (seuil du
    // masque lissé) entre un px dedans et un dehors.
    const cross = (j0, j1) => {
      const f0 = vals[j0]; const f1 = vals[j1];
      let t;
      if (f0 >= 0 && f1 >= 0) t = (level - f0) / ((f1 - f0) || 1e-9);
      else if (edge) t = (SAMPLE_EDGE - soft[j0]) / ((soft[j1] - soft[j0]) || 1e-9);
      else t = 0.5;
      return Math.min(1, Math.max(0, t));
    };
    for (let v = -1; v < h; v += 1) {
      for (let u = -1; u < w; u += 1) {
        const ja = J(u, v); const jb = ja + 1; const jd = ja + W2; const jc = jd + 1;  // ↖ ↗ ↘ ↙
        const a = vals[ja]; const b = vals[jb]; const c = vals[jc]; const d = vals[jd];
        const idx = (a >= level ? 8 : 0) | (b >= level ? 4 : 0) | (c >= level ? 2 : 0) | (d >= level ? 1 : 0);
        if (idx === 0 || idx === 15) continue;
        const top = () => at(2 * ja, u + cross(ja, jb), v);
        const right = () => at(2 * jb + 1, u + 1, v + cross(jb, jc));
        const bottom = () => at(2 * jd, u + cross(jd, jc), v + 1);
        const left = () => at(2 * ja + 1, u, v + cross(ja, jd));
        const center = (a + b + c + d) / 4 >= level;
        switch (idx) {
          case 1: case 14: segs.push([left(), bottom()]); break;
          case 2: case 13: segs.push([bottom(), right()]); break;
          case 3: case 12: segs.push([left(), right()]); break;
          case 4: case 11: segs.push([top(), right()]); break;
          case 6: case 9: segs.push([top(), bottom()]); break;
          case 7: case 8: segs.push([left(), top()]); break;
          case 5: if (center) { segs.push([left(), top()], [bottom(), right()]); } else { segs.push([left(), bottom()], [top(), right()]); } break;
          case 10: if (center) { segs.push([top(), right()], [left(), bottom()]); } else { segs.push([left(), top()], [bottom(), right()]); } break;
          default: break;
        }
      }
    }
    // Chaînage : chaque arête appartient à au plus deux segments (les deux cases qui la bordent).
    const byKey = new Map();
    segs.forEach(([k1, k2], j) => {
      [k1, k2].forEach(k => { const list = byKey.get(k); if (list) list.push(j); else byKey.set(k, [j]); });
    });
    const used = new Uint8Array(segs.length);
    const nextFrom = key => (byKey.get(key) || []).find(j => !used[j]);
    const paths = [];
    for (let j0 = 0; j0 < segs.length; j0 += 1) {
      if (used[j0]) continue;
      used[j0] = 1;
      const chain = [...segs[j0]];
      for (const forward of [true, false]) {
        for (;;) {
          const end = forward ? chain[chain.length - 1] : chain[0];
          const j = nextFrom(end);
          if (j === undefined) break;
          used[j] = 1;
          const other = segs[j][0] === end ? segs[j][1] : segs[j][0];
          if (forward) chain.push(other); else chain.unshift(other);
        }
      }
      const points = chain.map(k => pos.get(k));
      let length = 0;
      for (let i = 1; i < points.length; i += 1) length += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
      if (length < minLength) continue;
      paths.push(chaikin(points, chain[0] === chain[chain.length - 1], 2));
    }
    return { level: shown, paths };
  });
}

function chaikin(points, closed, passes) {
  let pts = points;
  for (let p = 0; p < passes; p += 1) {
    const out = closed ? [] : [pts[0]];
    const n = closed ? pts.length - 1 : pts.length - 1;
    for (let i = 0; i < n; i += 1) {
      const [x0, y0] = pts[i]; const [x1, y1] = pts[i + 1];
      out.push([0.75 * x0 + 0.25 * x1, 0.75 * y0 + 0.25 * y1], [0.25 * x0 + 0.75 * x1, 0.25 * y0 + 0.75 * y1]);
    }
    if (closed) out.push(out[0]); else out.push(pts[pts.length - 1]);
    pts = out;
  }
  return pts;
}

// Image de la carte (canevas), refaite à chaque changement d'affichage, de seuil, d'opacité ou de LUT.
export function attentionCanvas({ width: w, height: h, field, cover, zero }, settings) {
  const colors = Array.from({ length: 256 }, (_, i) => lutColor(settings.lut, [255, 255, 255], false, i / 255, settings.lutRange));
  const alpha = Array.from({ length: 256 }, (_, i) => 255 * attentionAlpha(i / 255, { ...settings, zero }));
  const rgba = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < field.length; i += 1) {
    if (!(cover[i] > 0) || Number.isNaN(field[i])) continue;
    const u = Math.min(255, Math.max(0, Math.round(field[i] * 255)));
    const c = colors[u];
    rgba[4 * i] = c[0]; rgba[4 * i + 1] = c[1]; rgba[4 * i + 2] = c[2]; rgba[4 * i + 3] = alpha[u] * cover[i];
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').putImageData(new ImageData(rgba, w, h), 0, 0);
  return canvas;
}
